import { CryptoConfigurationData, IconVariant } from '@caudena/core/ui-kit'
import {
  createCryptoConfiguration,
  createCryptoTypeGuards,
  CryptoTypes,
} from '@caudena/core/utils/currency'
import { DeepPartial } from '@caudena/core/types/DeepPartial'

const CRYPTO_CONFIG = {
  currencies: [
    {
      coin: 'BITCOIN',
      code: 'BTC',
      name: 'Bitcoin',
      exponent: 8,
      type: 'UTXO',
      index: 0,
    },
    {
      coin: 'ETHEREUM',
      code: 'ETH',
      name: 'Ethereum',
      exponent: 18,
      type: 'EVM',
      index: 1,
    },
    {
      coin: 'LITECOIN',
      code: 'LTC',
      name: 'Litecoin',
      exponent: 8,
      type: 'UTXO',
      index: 2,
    },
    {
      coin: 'DOGECOIN',
      code: 'DOGE',
      name: 'Dogecoin',
      exponent: 8,
      type: 'UTXO',
      index: 3,
    },
    {
      coin: 'BINANCE',
      code: 'BNB',
      name: 'Binance',
      exponent: 18,
      type: 'EVM',
      index: 4,
    },
    {
      coin: 'TRON',
      code: 'TRX',
      name: 'Tron',
      exponent: 18,
      type: 'EVM',
      index: 5,
    },
  ],
  symbols: {
    USD: '$',
    EUR: '€',
  },
} as const

export const crypto = createCryptoConfiguration(CRYPTO_CONFIG)

export type CryptoConfigurationTypes = typeof crypto.types

export type CryptoTypesResult = CryptoTypes<CryptoConfigurationTypes>

// Generate type guards based on the crypto configuration
const cryptoTypeGuards = createCryptoTypeGuards(crypto)

export const TOKEN_ICON_BY_CURRENCY: Record<
  CryptoTypesResult['CoinType'],
  IconVariant
> = {
  btc: 'Btc',
  eth: 'Eth',
  bnb: 'Bnb',
  trx: 'Trx',
  doge: 'Doge',
  ltc: 'Ltc',
}
// Re-export constants for easy access
export const {
  UTXO_COINS,
  EVM_COINS,
  COINS,
  EVM_BLOCKCHAINS,
  UTXO_BLOCKCHAINS,
  BLOCKCHAINS,
  COIN_CODES,
  COIN,
  CODE_BY_CURRENCY,
  CURRENCY_BY_CODE,
  COIN_NAMES,
  COIN_CODE_BY_INDEX,
  CURRENCY_UNIT_EXPONENT,
  CURRENCY_SYMBOLS,
  getCoinCode,
  getCoinKey,
  getCoinName,
  getCurrencyByIndex,
  getCurrency,
  getCoinExponent,
  isCoin,
  getCurrencySymbol,
  getRealCoinValue,
} = crypto

// Re-export type guards for easy access
export const {
  isUTXO,
  isEVM,
  isSupportedBlockchain,
  isUnsupportedBlockchain,
  isBlockchainEVM,
  isBlockchainUTXO,
} = cryptoTypeGuards

export const UIKIT_CRYPTO_CONFIGURATION = {
  assetIconByCurrency: TOKEN_ICON_BY_CURRENCY,
  supportedCoins: crypto.COINS,
  supportedBlockchains: crypto.BLOCKCHAINS,
  utxoBlockchains: crypto.UTXO_BLOCKCHAINS,
  evmBlockchains: crypto.EVM_BLOCKCHAINS,
  utxoCoins: crypto.UTXO_COINS,
  evmCoins: crypto.EVM_COINS,
  coinConstants: crypto.COIN_CODES,
  coinData: crypto.COIN,
  codeByCurrency: crypto.CODE_BY_CURRENCY,
  currencyByCode: crypto.CURRENCY_BY_CODE,
  coinNames: crypto.COIN_NAMES,
  coinCodeByIndex: crypto.COIN_CODE_BY_INDEX,
  currencyUnitExponent: crypto.CURRENCY_UNIT_EXPONENT,
  currencySymbols: crypto.CURRENCY_SYMBOLS,
} satisfies DeepPartial<CryptoConfigurationData>
